import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Components
import UserGrid from './componenets/UserGrid';
import PasswordDialog from './componenets/PasswordDialog';
import LanguageSwitcher from '../../../../i18n/LanguageSwitcher';
import HexagonDecoration from '../LoginPage/components/HexagonDecoration';
import OrbitLogo from '../../../../components/common/OrbitLogo';
import ThemeToggleButton from '../../../../components/common/ThemeToggleButton';

// Hooks
import { useAuth } from '../../../../context/AuthContext';
import { useSnackbar } from '../../../../hooks/mui/useSnackBar';
import { useNavigationStack } from '../../../../context/NavigationStackContext';
import { SWITCH_SESSION_KEY } from '../../services/authService';

export default function ChangeUserPage() {
  const { t } = useTranslation(['auth', 'common']);
  const theme = useTheme();
  const hexColor = alpha(theme.palette.primary.main, 0.1);
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = location.state?.redirectTo || '/home';
  const { user, logout, login, loginWithPin } = useAuth();
  const { showSuccess, showError } = useSnackbar();
  const { clearStack } = useNavigationStack();

  const [availableUsers, setAvailableUsers] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [originalDepartment, setOriginalDepartment] = useState('');
  const [loading, setLoading] = useState(true);
  const [isAuthLoading, setIsAuthLoading] = useState(false);
  const [authLoadingKey, setAuthLoadingKey] = useState('auth:change_user.loading.authenticating');
  const [selectedUser, setSelectedUser] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [passwordDialogError, setPasswordDialogError] = useState(null);

  useEffect(() => {
    const initialize = async () => {
      try {
        if (localStorage.getItem('orbit_perm_infoscreen') && !location.state?.redirectTo) {
          if (user) await logout();
          navigate('/infoscreens/show');
          return;
        }

        const savedData = localStorage.getItem('orbit_users');
        if (!savedData) {
          await logout();
          navigate('/login');
          return;
        }

        const userData = JSON.parse(savedData);
        if (!userData.available_users?.length) {
          await logout();
          navigate('/login');
          return;
        }

        setCurrentUser(userData.current_user);
        setAvailableUsers(userData.available_users);
        setOriginalDepartment(userData.original_department);

        if (user) {
          await logout();
          showSuccess(t('auth:change_user.messages.logout_success'));
        }
      } catch (error) {
        showError(t('auth:change_user.messages.failed_load_staff'));
        await logout();
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };

    initialize();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUserSelect = useCallback((userToSelect) => {
    setSelectedUser(userToSelect);
    setPasswordDialogError(null);
    setPasswordDialogOpen(true);
  }, []);

  const handlePasswordDialogClose = useCallback(() => {
    setPasswordDialogOpen(false);
    setSelectedUser(null);
    setPasswordDialogError(null);
  }, []);

  const completeNavigation = useCallback(() => {
    setAuthLoadingKey('auth:change_user.logging_in');
    setIsAuthLoading(true);
    showSuccess(t('auth:change_user.messages.login_success', { name: selectedUser?.name }));
    clearStack();
    setTimeout(() => navigate(redirectTo), 100);
  }, [selectedUser, showSuccess, t, clearStack, navigate, redirectTo]);

  const handlePasswordConfirm = useCallback(async (password) => {
    if (!selectedUser || !password.trim()) return;

    setIsAuthLoading(true);
    setLoggingIn(true);
    setPasswordDialogError(null);

    try {
      const result = await login({
        department_name: originalDepartment,
        staff_member_name: selectedUser.name,
        password: password,
      });

      if (!result.success) {
        throw new Error(result.error);
      }

      setPasswordDialogOpen(false);
      // If the user needs to set up a PIN, the global MainLayout gate will
      // redirect them to /force-pin once they land on redirectTo.
      completeNavigation();
    } catch (error) {
      const errorMessage = error.message || t('auth:change_user.messages.login_failed');
      setPasswordDialogError(errorMessage);
      setIsAuthLoading(false);
      setAuthLoadingKey('auth:change_user.loading.authenticating');
    } finally {
      setLoggingIn(false);
    }
  }, [selectedUser, originalDepartment, t, login, completeNavigation]);

  const handlePinConfirm = useCallback(async (pin) => {
    if (!selectedUser || !pin) return;

    setLoggingIn(true);
    setPasswordDialogError(null);

    try {
      const result = await loginWithPin({
        department_name: originalDepartment,
        staff_member_name: selectedUser.name,
        pin,
      });

      if (!result.success) {
        const error = new Error(result.error);
        error.code = result.code;
        throw error;
      }

      setPasswordDialogOpen(false);
      completeNavigation();
    } catch (error) {
      const errorMessage = error.message || t('auth:change_user.messages.login_failed');
      setPasswordDialogError(errorMessage);
      throw error;
    } finally {
      setLoggingIn(false);
    }
  }, [selectedUser, originalDepartment, t, loginWithPin, completeNavigation]);

  const handleClearPasswordDialogError = useCallback(() => {
    setPasswordDialogError(null);
  }, []);

  const isInfoscreen = !!localStorage.getItem('orbit_perm_infoscreen');

  const handleBackToLogin = useCallback(() => {
    localStorage.removeItem('orbit_users');
    sessionStorage.removeItem(SWITCH_SESSION_KEY);
    navigate(isInfoscreen ? '/infoscreens/show' : '/login');
  }, [navigate, isInfoscreen]);

  const loadingScreen = (message) => (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          {message}
        </Typography>
      </Box>
    </Box>
  );

  if (loading) return loadingScreen(t('auth:change_user.loading.wait_message'));
  if (isAuthLoading) return loadingScreen(t(authLoadingKey));

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        bgcolor: 'background.default',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <HexagonDecoration position="top-left" color={hexColor} />
      <HexagonDecoration position="bottom-right" color={hexColor} />

      {/* Top bar */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          px: { xs: 2, sm: 4 },
          pt: 3,
          pb: 2,
        }}
      >
        <OrbitLogo height={34} />
        <Box display="flex" gap={{ xs: 1, sm: 3 }} alignItems="center" flexWrap="wrap" justifyContent="flex-end" sx={{ flex: 1, minWidth: 0 }}>
          <ThemeToggleButton variant="outlined" />
          <Box width="130px" sx={{ flexShrink: 0 }}>
            <LanguageSwitcher />
          </Box>
          <Button variant="outlined" color="error" onClick={handleBackToLogin} sx={{ whiteSpace: 'nowrap', paddingTop: '2px', paddingBottom: '1px', flexShrink: 0 }}>
            {t(isInfoscreen ? 'auth:change_user.back_to_infoscreen' : 'auth:change_user.back_to_login')}
          </Button>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 3, flex: 1, px: 4, pb: 4 }}>
        {/* <Typography variant="h4" fontWeight="bold" mb={0.5}>
          {t('auth:change_user.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('auth:change_user.subtitle')}
        </Typography> */}

        <UserGrid
          users={availableUsers}
          onUserSelect={handleUserSelect}
        />
      </Box>

      <PasswordDialog
        open={passwordDialogOpen}
        onClose={handlePasswordDialogClose}
        onConfirm={handlePasswordConfirm}
        onPinConfirm={handlePinConfirm}
        pinAvailable={!!sessionStorage.getItem(SWITCH_SESSION_KEY) && !!selectedUser?.pin_enabled}
        loading={loggingIn}
        targetUser={selectedUser?.name}
        staffMember={selectedUser}
        departmentName={originalDepartment}
        showUserInfo={true}
        serverError={passwordDialogError}
        onClearError={handleClearPasswordDialogError}
      />
    </Box>
  );
}
